import {
  importBibliography,
  uploadDocumentDirect,
} from "@/features/clipboard/api/clipboard";
import { useInvalidateClipboard } from "@/features/clipboard/clipboardQueries";
import { collectDroppedFiles } from "@/features/clipboard/utils/collectDroppedFiles";
import { useWorkspaceStorage } from "@/features/usage/hooks/useLimit";
import { reportLimitError } from "@/features/usage/limitErrors";
import { useCallback, useRef, useState } from "react";

export type UploadItem = {
  name: string;
  status: "uploading" | "done" | "error";
  phase?: "uploading" | "adding";
  message?: string;
  /** Retained so a failed upload can be retried without re-picking the file. */
  file: File;
};

type UseClipboardUploadsOptions = {
  path: string;
  user: unknown;
  onError: (message: string) => void;
};

export const useClipboardUploads = ({
  path,
  user,
  onError,
}: UseClipboardUploadsOptions) => {
  const invalidateClipboard = useInvalidateClipboard();
  const { refetchUsage, isAtLimit: storageAtLimit } = useWorkspaceStorage();
  const [dragActive, setDragActive] = useState(false);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const uploadOne = useCallback(
    async (file: File) => {
      // For folder uploads, webkitRelativePath gives "folder/sub/file.pdf";
      // use the directory portion as the folder_path.
      const relativePath =
        (file as File & { webkitRelativePath?: string }).webkitRelativePath || "";
      const folderFromPath = relativePath
        ? relativePath.split("/").slice(0, -1).join("/")
        : "";
      const uploadFolder = folderFromPath || path;

      try {
        if (/\.(bib|ris)$/i.test(file.name)) {
          await importBibliography(file, uploadFolder);
        } else {
          const result = await uploadDocumentDirect(file, uploadFolder, (phase) => {
            setUploads((prev) =>
              prev.map((item) =>
                item.file === file
                  ? {
                      ...item,
                      phase: phase === "uploading" ? "uploading" : "adding",
                    }
                  : item,
              ),
            );
          });
          if (result.status === "duplicate") {
            setUploads((prev) =>
              prev.map((item) =>
                item.file === file
                  ? {
                      ...item,
                      message: result.message || "Already in this folder",
                    }
                  : item,
              ),
            );
          }
        }
        setUploads((prev) =>
          prev.map((u) => (u.file === file ? { ...u, status: "done" } : u)),
        );
        // Reveal the row immediately; durable rich processing and indexing
        // continue visibly in the document status.
        invalidateClipboard();
        refetchUsage();
        setTimeout(
          () => setUploads((prev) => prev.filter((u) => u.file !== file)),
          2500,
        );
      } catch (e) {
        const limitMessage = reportLimitError(e, "uploads", {
          refetchUsage,
          quotaAlertVisible: storageAtLimit,
        });
        setUploads((prev) =>
          prev.map((u) =>
            u.file === file
              ? {
                  ...u,
                  status: "error",
                  message: limitMessage ?? (e instanceof Error ? e.message : "Upload failed"),
                }
              : u,
          ),
        );
      }
    },
    [invalidateClipboard, path, refetchUsage, storageAtLimit],
  );

  const runUploads = useCallback(
    async (files: FileList | File[]) => {
      if (!user) return;
      if (storageAtLimit) {
        onError("Workspace storage is full.");
        return;
      }
      const list = Array.from(files);
      // Filter out hidden files and directory entries (size 0 with no extension)
      const accepted = list.filter((f) => {
        if (!f.name || f.name.startsWith(".")) return false;
        if (f.size === 0 && !f.name.includes(".")) return false;
        if (f.size === 0 && f.type === "") return false;
        return true;
      });
      if (accepted.length === 0) {
        onError("No files selected");
        return;
      }

      // Newly added files join the tray; existing rows (e.g. prior errors) stay.
      setUploads((prev) => [
        ...prev.filter((u) => !accepted.includes(u.file)),
        ...accepted.map((f) => ({
          name: f.name,
          status: "uploading" as const,
          phase: "uploading" as const,
          file: f,
        })),
      ]);

      // Direct uploads bypass Cloud Run and go straight to GCS. Eight parallel
      // transfers keeps large folder adds moving without creating an unbounded
      // number of completion requests or holding every file in server memory.
      const concurrency = 8;
      const semaphore = { count: 0 };
      await Promise.all(
        accepted.map(async (file) => {
          while (semaphore.count >= concurrency) {
            await new Promise((r) => setTimeout(r, 50));
          }
          semaphore.count++;
          try {
            await uploadOne(file);
          } finally {
            semaphore.count--;
          }
        }),
      );
    },
    [onError, uploadOne, user, storageAtLimit],
  );

  const retryUpload = useCallback(
    (item: UploadItem) => {
      setUploads((prev) =>
        prev.map((u) =>
          u.file === item.file
            ? {
                ...u,
                status: "uploading",
                phase: "uploading",
                message: undefined,
              }
            : u,
        ),
      );
      void uploadOne(item.file);
    },
    [uploadOne],
  );

  const dismissUpload = useCallback((item: UploadItem) => {
    setUploads((prev) => prev.filter((u) => u.file !== item.file));
  }, []);

  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const files = await collectDroppedFiles(e.dataTransfer);
      if (files.length > 0) void runUploads(files);
    },
    [runUploads],
  );

  return {
    uploads,
    dragActive,
    setDragActive,
    onDrop,
    runUploads,
    retryUpload,
    dismissUpload,
    pdfInputRef,
    folderInputRef,
    storageAtLimit,
  };
};
